import React, { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ChevronRightIcon, ChevronLeftIcon, CreditCardIcon, ShieldCheckIcon, InfoIcon } from 'lucide-react';
import { useTeamRegistrationStore } from '../../store/teamRegistrationStore';
import { useClubStore } from '../../store/clubStore';
import { useAssociationStore } from '../../store/associationStore';
import { useDialogStore } from '../../store/dialogStore';
import axiosInstance from '../../axiosConfig';

interface BankTabProps {
    onNext?: () => void;
    onPrevious?: () => void;
    showNextButton?: boolean;
    showPreviousButton?: boolean;
}

const BankTab: React.FC<BankTabProps> = ({ onNext, onPrevious, showNextButton = true, showPreviousButton = false }) => {
    const [ibanValid, setIbanValid] = useState<boolean>(false);
    const iban = useTeamRegistrationStore((state) => state.iban);
    const setIban = useTeamRegistrationStore((state) => state.setIban);
    const myClub = useClubStore((state) => state.myClub);
    const association = useAssociationStore((state) => state.association);
    const openDialog = useDialogStore((state) => state.openDialog);

    // For DE IBANs the required length is 22 characters
    const REQUIRED_IBAN_LENGTH = 22;

    const sanitizeIban = (value: string): string => {
        // Remove spaces, force uppercase, allow only A-Z and 0-9, trim to max 34 chars
        const cleaned = value.replace(/\s+/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        return cleaned.slice(0, 34);
    };

    const handleIbanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = sanitizeIban(e.target.value);
        setIban(sanitized);
    };

    useEffect(() => {
        const clubIbanRaw = myClub?.bank?.iban || '';
        const clubIban = sanitizeIban(clubIbanRaw);
        if (clubIban && iban !== clubIban) {
            setIban(clubIban);
        }
    }, [myClub?.bank?.iban]);

    // Debounced IBAN validation (500ms after change)
    useEffect(() => {
        if (!iban) {
            setIbanValid(false);
            return;
        }

        // Only start server validation once the required length is reached
        if (iban.length < REQUIRED_IBAN_LENGTH) {
            setIbanValid(false);
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const res = await axiosInstance.post('iban/validate', { iban });
                setIbanValid(Boolean(res.data?.valid));
            } catch (error) {
                setIbanValid(false);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [iban]);


    const handleNextStep = () => {
        if (ibanValid === false) {
            openDialog(
                <div className="text-md py-4 grid grid-cols-1 gap-1 text-left">
                    <div className="text-lg font-bold text-red-600">Bitte IBAN korrigieren</div>
                    <div className="text-sm font-medium">
                        Die Validierung Ihrer IBAN ist fehlgeschlagen. Bitte geben Sie eine gültige IBAN ein.
                    </div>
                </div>,
                () => { },
                true
            );
            return;
        }
        if (onNext) {
            onNext();
        }
    };

    const handlePreviousStep = () => {
        if (onPrevious) {
            onPrevious();
        }
    };

    return (
        <div className="">
            {/* Header */}
            <div className="mb-8">


                {/* Information Box */}
                <div className="bg-gradient-to-r from-blue-500/5 to-blue-500/10 border border-blue-500/20 p-6 rounded-xl">
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <InfoIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        {association?.name && (
                            <div>
                                <h3 className="font-semibold text-blue-600 mb-2 text-sm">Wichtige Information</h3>
                                <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                                    <span className="font-medium text-gray-900">Die Mannschaftsmeldung</span> kann nur unter Erteilung einer{' '}
                                    <span className="font-bold text-blue-600 bg-blue-500/10 px-2 py-1 rounded-md">SEPA-Einzugsermächtigung</span> für den{' '}
                                    <span className="font-semibold text-blue-600">{association?.name}</span> erfolgen.
                                </p>
                                <div className="bg-white/60 rounded-lg p-3 border border-[#52A445]/20">
                                    <p className="text-xs text-gray-700 font-medium">
                                        <ShieldCheckIcon className="w-3 h-3 inline mr-1 text-blue-600" />
                                        Bitte geben Sie Ihre <span className="font-bold text-blue-600">IBAN</span> vollständig und ohne Leerzeichen ein.
                                    </p>
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Eine korrekt eingegebene IBAN ist erforderlich, damit wir die SEPA-Lastschrift sicher verarbeiten können.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
                {/* IBAN Field */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="text-blue-600 font-bold text-sm">IB</span>
                        </div>
                        <div>
                            <label htmlFor="iban" className="block text-sm font-semibold text-gray-900">
                                IBAN (International Bank Account Number)
                            </label>
                            <p className="text-xs text-gray-500">Geben Sie Ihre IBAN ohne Leerstellen ein</p>
                        </div>
                    </div>
                    <Input
                        id="iban"
                        type="text"
                        value={iban || ''}
                        onChange={handleIbanChange}
                        onKeyDown={(e) => {
                            if (e.key === ' ') {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData('text');
                            setIban(sanitizeIban(text));
                        }}
                        placeholder="DE00 0000 0000 0000 0000 00"
                        className={`w-full text-lg font-mono tracking-wider ${(iban.length >= REQUIRED_IBAN_LENGTH && ibanValid === false) ? 'border-red-500' : ''}`}
                    />
                    {(iban.length >= REQUIRED_IBAN_LENGTH && ibanValid === false) && (
                        <div className="mt-1 text-xs text-red-600">Das Format der IBAN ist ungültig.</div>
                    )}
                </div>


                {/* Security Note */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center mt-0.5">
                            <span className="text-amber-600 text-xs font-bold">!</span>
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-amber-800 mb-1">Datensicherheit</h4>
                            <p className="text-xs text-amber-700">
                                Ihre Bankdaten werden sicher übertragen und nur für die SEPA-Einzugsermächtigung verwendet.
                                Alle Verbindungen sind SSL-verschlüsselt.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Buttons */}
            {(showNextButton || showPreviousButton) && (
                <div className="mt-8 flex justify-between">
                    {showPreviousButton && (
                        <Button
                            onClick={handlePreviousStep}
                            variant="outline"
                            className="px-6 flex items-center gap-2 border-gray-300 hover:border-[#52A445] hover:bg-[#52A445]/5"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                            Zurück
                        </Button>
                    )}
                    <div className="flex-1"></div>
                    {showNextButton && (
                        <Button
                            onClick={handleNextStep}
                            className="px-6 flex items-center gap-2 bg-[#52A445] hover:bg-[#52A445]/90"
                        >
                            Weiter
                            <ChevronRightIcon className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BankTab;
